import { useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Copy, Check, WhatsappLogo, ArrowSquareOut, Warehouse, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Dialog to create a temporary public "lot" share link from the currently
 * filtered products list. The parent passes in the array of products that will
 * be snapshotted so the wholesale buyer sees exactly this set. Prices are
 * hidden on the public page — only a single total (set here) is shown.
 */
export function ShareLotDialog({ open, onOpenChange, products }) {
  const [step, setStep] = useState("form"); // form | success
  const [title, setTitle] = useState("Lote especial");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("MXN");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // { token, expires_at, ... }
  const [copied, setCopied] = useState(false);

  const totalStock = useMemo(
    () => (products || []).reduce((a, p) => a + Number(p.stock || 0), 0),
    [products]
  );

  const reset = () => {
    setStep("form");
    setTitle("Lote especial");
    setPrice("");
    setCurrency("MXN");
    setNotes("");
    setResult(null);
  };

  const close = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const create = async () => {
    if (!products || products.length === 0) {
      toast.error("La lista está vacía, ajusta tus filtros primero.");
      return;
    }
    const priceNum = Number(price);
    if (!priceNum || priceNum <= 0) {
      toast.error("Ingresa el precio total del lote.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/lots", {
        product_ids: products.map((p) => p.id).filter(Boolean),
        lot_price: priceNum,
        lot_currency: currency,
        title: title || "Lote especial",
        notes,
      });
      setResult(data);
      setStep("success");
      toast.success("Link creado — válido por 6 días");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el link");
    } finally {
      setSaving(false);
    }
  };

  const shareUrl = result ? `${window.location.origin}/lote/${result.token}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Link copiado");
    } catch {
      toast.error("No se pudo copiar el link");
    }
  };

  const whatsappText = () => {
    const priceStr = `${currency === "USD" ? "$" : "MX$"}${Number(result?.lot_price || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const msg = `Hola! Te comparto una oferta de lote:\n\n*${result?.title || "Lote especial"}*\n${result?.product_count || 0} productos · ${totalStock.toLocaleString("es-MX")} piezas\nPrecio total: ${priceStr}\n\nContenido completo con fotos y stock:\n${shareUrl}\n\nLink válido por 6 días.`;
    return encodeURIComponent(msg);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg" data-testid="lot-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package size={20} weight="duotone" className="text-[#002FA7]" />
            {step === "form" ? "Compartir lote a mayoreo" : "Link listo para compartir"}
          </DialogTitle>
        </DialogHeader>

        {step === "form" && (
          <div className="space-y-4">
            {/* Summary of what's being shared */}
            <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
                Contenido del lote (según tus filtros actuales)
              </div>
              <div className="grid grid-cols-2 gap-3">
                <MiniStat label="Productos" value={(products || []).length} icon={<Package size={12} weight="bold" />} />
                <MiniStat label="Piezas totales" value={totalStock} icon={<Warehouse size={12} weight="bold" />} />
              </div>
            </div>

            {/* Title */}
            <div className="space-y-1.5">
              <Label htmlFor="lot-title">Título del lote</Label>
              <Input
                id="lot-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Lote especial"
                data-testid="lot-title-input"
              />
            </div>

            {/* Price + currency */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="lot-price">Precio total del lote *</Label>
                <Input
                  id="lot-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="ej. 45000"
                  data-testid="lot-price-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Moneda</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger data-testid="lot-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MXN">MXN</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label htmlFor="lot-notes">Notas (opcional) — se muestran en el link</Label>
              <textarea
                id="lot-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Ej. Precio incluye envío. Válido para pago en una sola exhibición."
                className="w-full text-sm border border-zinc-200 rounded-md p-2 focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/40 resize-none"
                data-testid="lot-notes-input"
              />
            </div>

            <div className="text-[11px] text-zinc-500 flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded p-2.5">
              <Clock size={13} weight="bold" className="text-amber-600 shrink-0 mt-0.5" />
              El link será válido por 6 días. La lista de productos queda congelada; si cambia el inventario después, tu comprador seguirá viendo esta misma selección.
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => close(false)} data-testid="lot-cancel-btn">Cancelar</Button>
              <Button onClick={create} disabled={saving} data-testid="lot-create-btn" className="bg-[#002FA7] hover:bg-[#0043c2]">
                {saving ? "Creando…" : "Generar link"}
              </Button>
            </div>
          </div>
        )}

        {step === "success" && result && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start gap-2">
              <Check size={16} weight="bold" className="text-emerald-600 mt-0.5" />
              <div className="text-sm text-emerald-900">
                Link creado — <span className="font-semibold">{result.product_count} productos</span> · precio total{" "}
                <span className="font-semibold">
                  {currency === "USD" ? "$" : "MX$"}{Number(result.lot_price || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>URL para compartir</Label>
              <div className="flex gap-2">
                <Input value={shareUrl} readOnly className="font-mono text-xs" data-testid="lot-share-url" />
                <Button variant="outline" onClick={copyLink} className="shrink-0" data-testid="lot-copy-btn">
                  {copied ? <Check size={14} weight="bold" className="text-emerald-500" /> : <Copy size={14} weight="bold" />}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <a
                href={`https://wa.me/?text=${whatsappText()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 text-sm font-medium"
                data-testid="lot-whatsapp-btn"
              >
                <WhatsappLogo size={16} weight="bold" />
                WhatsApp
              </a>
              <a
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-zinc-200 text-zinc-700 hover:bg-zinc-50 text-sm font-medium"
                data-testid="lot-open-btn"
              >
                <ArrowSquareOut size={16} weight="bold" />
                Abrir vista pública
              </a>
            </div>

            <div className="text-[11px] text-zinc-500 flex items-start gap-1.5">
              <Clock size={12} weight="bold" className="shrink-0 mt-0.5" />
              Expira el {result.expires_at ? new Date(result.expires_at).toLocaleString("es-MX") : "—"}. Después necesitarás generar uno nuevo.
            </div>

            <div className="flex justify-end pt-1">
              <Button variant="outline" onClick={() => close(false)} data-testid="lot-done-btn">Listo</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value, icon }) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold inline-flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="font-heading font-extrabold text-lg text-[#002FA7] numeric ml-auto">
        {typeof value === "number" ? value.toLocaleString("es-MX") : value}
      </div>
    </div>
  );
}
